/**
 * PWA bootstrap — service-worker registration + Install button helper.
 *
 * Design notes:
 *   • SW is registered ONLY when we're actually served over HTTPS (or on
 *     http://localhost). This prevents preview.emergentagent.com from
 *     accidentally caching the code-server variant of the app.
 *   • On update we soft-nudge the app to reload via 'controllerchange'.
 *     No auto-refresh mid-user-flow.
 *   • We surface the browser's `beforeinstallprompt` event as a
 *     `window.__aeduInstallPrompt` handle so any component (e.g. a
 *     future Install CTA banner) can trigger the install dialog.
 */

export function registerServiceWorker() {
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;

  // Only register on https:// or localhost — never on http:// production
  // (browsers block SWs there anyway; we skip early to keep logs clean).
  const { protocol, hostname } = window.location;
  const isSecure = protocol === 'https:' || hostname === 'localhost' || hostname === '127.0.0.1';
  if (!isSecure) return;

  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js', { scope: '/' })
      .then((reg) => {
        // Skip-waiting hand-off: when a new SW is installed AND we already
        // had one, tell it to swap in immediately so users don't get stuck
        // on stale JS after a deploy.
        if (reg.waiting) reg.waiting.postMessage('SKIP_WAITING');
        reg.addEventListener('updatefound', () => {
          const installing = reg.installing;
          if (!installing) return;
          installing.addEventListener('statechange', () => {
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              installing.postMessage('SKIP_WAITING');
            }
          });
        });
      })
      .catch(() => { /* swallow — never break the app on a SW hiccup */ });

    let refreshing = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    });
  });
}

/**
 * Capture the browser's install prompt so any component can trigger it.
 * Usage:
 *   window.__aeduInstallPrompt?.prompt();
 * (Chrome/Edge/Android only — iOS has no equivalent API and we render
 * a small "Add to Home Screen" hint instead in the UI layer.)
 */
export function captureInstallPrompt() {
  if (typeof window === 'undefined') return;
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    window.__aeduInstallPrompt = {
      prompt: () => e.prompt(),
      whenChoice: () => e.userChoice,
    };
    window.dispatchEvent(new CustomEvent('aedu:installable'));
  });
  window.addEventListener('appinstalled', () => {
    window.__aeduInstallPrompt = null;
    window.dispatchEvent(new CustomEvent('aedu:installed'));
  });
}

// Convenience — is the app currently running as an installed PWA?
export function isStandaloneDisplay() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches
    || window.navigator.standalone === true   // iOS Safari
  );
}
